/**
 * Completion Step
 * 
 * Final step with context-aware CTA and navigation.
 * Issue #563 - First-Run Onboarding Flow
 */

import type { OnboardingContext } from '@/lib/api/auth';

interface CompletionStepProps {
  context: OnboardingContext;
  onComplete: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}

export default function CompletionStep({ context, onComplete, onBack, isSubmitting }: CompletionStepProps) {
  // Context-aware content
  const getContent = () => {
    switch (context.type) {
      case 'club':
        return {
          title: 'You are all set!',
          subtitle: `Ready to connect with ${context.club_name}?`,
          cta: 'Go to Club',
        };
      case 'event':
        return {
          title: 'You are ready!',
          subtitle: `Let us check out ${context.event_name}.`,
          cta: 'View Event',
        };
      case 'founder':
        return {
          title: 'Time to build',
          subtitle: 'Ready to create your community?',
          cta: context.club_id ? 'Go to Club' : 'Create Club',
        };
      case 'clean':
      default:
        return {
          title: 'Welcome aboard!',
          subtitle: 'Explore clubs and events in your area.',
          cta: 'Explore',
        };
    }
  };

  const { title, subtitle, cta } = getContent();

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold text-[--oro-text-primary]">
          {title}
        </h2>
        <p className="text-[--oro-text-secondary]">
          {subtitle}
        </p>
      </div>

      {/* Success illustration */}
      <div className="h-48 rounded-xl bg-[--oro-surface-secondary] flex items-center justify-center">
        <span className="text-[--oro-text-tertiary] text-sm">
          Success celebration
        </span>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="px-6 py-2.5 rounded-lg bg-[--oro-surface-secondary] text-[--oro-text-primary] font-medium hover:bg-[--oro-surface-tertiary] transition-colors disabled:opacity-50"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onComplete}
          disabled={isSubmitting}
          className="px-6 py-2.5 rounded-lg bg-[--oro-accent-primary] text-[--oro-text-on-accent] font-medium hover:bg-[--oro-accent-hover] transition-colors disabled:opacity-50 min-w-[120px]"
        >
          {isSubmitting ? 'Loading...' : cta}
        </button>
      </div>
    </div>
  );
}
